/**
 * Auth provider + `useAuth` hook for the admin panel.
 *
 * Login hits the REAL controller route `POST /admin/auth/login`
 * (apps/api/src/admin/admin.controller.ts) which returns
 * `{ accessToken, refreshToken, user }`. The access token is held in memory
 * AND mirrored to localStorage so a page refresh stays signed in; the refresh
 * token is persisted so an expiring access token can be exchanged silently.
 *
 * The provider wires two handlers into the API client:
 *  - refresh: a 401 first tries `POST /auth/refresh`, and the request is
 *    replayed if that works. Access tokens last 15 minutes, so without this an
 *    operator is signed out mid-edit four times an hour (18_ADMIN_SESSION.md).
 *  - unauthorized: only when the refresh is unavailable or rejected does the
 *    session clear and the app redirect to /login.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { post } from './api';
import {
  authStorageKeys,
  setAccessToken,
  setRefreshHandler,
  setUnauthorizedHandler,
} from './auth-token';
import type {
  AdminLoginRequest,
  AdminLoginResponse,
  AdminUser,
  UserRole,
} from '../types';

interface AuthState {
  user: AdminUser | null;
  accessToken: string | null;
}

export interface AuthContextValue {
  user: AdminUser | null;
  role: UserRole | null;
  /** True once a valid access token + user are loaded. */
  isAuthenticated: boolean;
  /** Convenience: role === 'SUPER_ADMIN' (config screen is Super-only). */
  isSuperAdmin: boolean;
  /** Authenticate; resolves with the user or throws ApiError on failure. */
  login: (credentials: AdminLoginRequest) => Promise<AdminUser>;
  /** Clear the session (local only; no server round-trip required). */
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readInitialState(): AuthState {
  try {
    const accessToken = localStorage.getItem(authStorageKeys.access);
    const rawUser = localStorage.getItem(authStorageKeys.user);
    if (accessToken && rawUser) {
      const user = JSON.parse(rawUser) as AdminUser;
      setAccessToken(accessToken);
      return { accessToken, user };
    }
  } catch {
    /* corrupted storage — fall through to signed-out */
  }
  return { accessToken: null, user: null };
}

function persist(
  accessToken: string,
  refreshToken: string,
  user: AdminUser,
): void {
  localStorage.setItem(authStorageKeys.access, accessToken);
  localStorage.setItem(authStorageKeys.refresh, refreshToken);
  localStorage.setItem(authStorageKeys.user, JSON.stringify(user));
}

function clearStorage(): void {
  localStorage.removeItem(authStorageKeys.access);
  localStorage.removeItem(authStorageKeys.refresh);
  localStorage.removeItem(authStorageKeys.user);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(readInitialState);

  const adopt = useCallback((res: AdminLoginResponse): AdminUser => {
    setAccessToken(res.accessToken);
    persist(res.accessToken, res.refreshToken, res.user);
    setState({ accessToken: res.accessToken, user: res.user });
    return res.user;
  }, []);

  const forget = useCallback(() => {
    clearStorage();
    setAccessToken(null);
    setState({ accessToken: null, user: null });
  }, []);

  const logout = useCallback(() => {
    // Best-effort server-side revoke: `POST /auth/logout` kills the whole
    // refresh family, so a signed-out session cannot be resumed with the token
    // that was just in localStorage. Idempotent and never throws by design
    // (auth.service.ts:327) — and signing out must not fail because the
    // network did, so the local clear happens either way.
    const refreshToken = localStorage.getItem(authStorageKeys.refresh);
    if (refreshToken) {
      void post('/auth/logout', { refreshToken }, { noAuthRetry: true }).catch(
        () => undefined,
      );
    }
    forget();
  }, [forget]);

  const login = useCallback(
    async (credentials: AdminLoginRequest): Promise<AdminUser> => {
      // noAuthRetry: a 401 here is a wrong password, not an expired session.
      const res = await post<AdminLoginResponse>(
        '/admin/auth/login',
        credentials,
        { noAuthRetry: true },
      );
      return adopt(res);
    },
    [adopt],
  );

  // Exchange the stored refresh token for a fresh pair. Returns false to let
  // the API client fall through to the unauthorized handler. Never throws:
  // the caller is deciding whether to retry, not reporting an error.
  const refresh = useCallback(async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem(authStorageKeys.refresh);
    if (!refreshToken) return false;
    try {
      adopt(
        await post<AdminLoginResponse>(
          '/auth/refresh',
          { refreshToken },
          { noAuthRetry: true },
        ),
      );
      return true;
    } catch {
      // Expired, revoked, or reuse detected — the session is genuinely over.
      forget();
      return false;
    }
  }, [adopt, forget]);

  // Register both API-client handlers. A 401 tries `refresh` first; only if
  // that returns false does `logout` run and the component tree's
  // <Navigate>/ProtectedLayout bounce to /login on the next render.
  useEffect(() => {
    setRefreshHandler(refresh);
    setUnauthorizedHandler(() => forget());
    return () => {
      setRefreshHandler(null);
      setUnauthorizedHandler(null);
    };
  }, [refresh, forget]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: state.user,
      role: state.user?.role ?? null,
      isAuthenticated: Boolean(state.accessToken && state.user),
      isSuperAdmin: state.user?.role === 'SUPER_ADMIN',
      login,
      logout,
    }),
    [state, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an <AuthProvider>.');
  }
  return ctx;
}
